import type { FuturesBar, IndicatorKey } from "@/types/futuresChart"
import type { SeriesOverlayKey } from "@/types/seriesChart"

export interface IndicatorPoint {
  time: string
  value: number | null
}

/** Object-form series point used by feed overlay calculations. */
export interface SeriesValuePoint {
  time: string
  value: number
}

export function simpleMovingAverageOnSeries(
  points: SeriesValuePoint[],
  period: number,
): IndicatorPoint[] {
  let sum = 0
  return points.map((point, index) => {
    sum += point.value
    if (index >= period) sum -= points[index - period].value
    return {
      time: point.time,
      value: index >= period - 1 ? sum / period : null,
    }
  })
}

export function exponentialMovingAverageOnSeries(
  points: SeriesValuePoint[],
  period: number,
): IndicatorPoint[] {
  const multiplier = 2 / (period + 1)
  let current: number | null = null
  return points.map((point) => {
    current = current === null ? point.value : point.value * multiplier + current * (1 - multiplier)
    return { time: point.time, value: current }
  })
}

export function calculateSeriesOverlay(
  points: SeriesValuePoint[],
  key: SeriesOverlayKey,
): IndicatorPoint[] {
  switch (key) {
    case "sma4":
      return simpleMovingAverageOnSeries(points, 4)
    case "sma12":
      return simpleMovingAverageOnSeries(points, 12)
    case "sma20":
      return simpleMovingAverageOnSeries(points, 20)
    case "ema20":
      return exponentialMovingAverageOnSeries(points, 20)
  }
}

export function simpleMovingAverage(bars: FuturesBar[], period: number): IndicatorPoint[] {
  return simpleMovingAverageOnSeries(
    bars.map((bar) => ({ time: bar.time, value: bar.close })),
    period,
  )
}

export function exponentialMovingAverage(bars: FuturesBar[], period: number): IndicatorPoint[] {
  return exponentialMovingAverageOnSeries(
    bars.map((bar) => ({ time: bar.time, value: bar.close })),
    period,
  )
}

export function calculateIndicator(bars: FuturesBar[], key: IndicatorKey): IndicatorPoint[] {
  switch (key) {
    case "sma20":
      return simpleMovingAverage(bars, 20)
    case "sma50":
      return simpleMovingAverage(bars, 50)
    case "ema20":
      return exponentialMovingAverage(bars, 20)
  }
}

export const INDICATOR_LABELS: Record<IndicatorKey, string> = {
  sma20: "SMA 20",
  sma50: "SMA 50",
  ema20: "EMA 20",
}
